import type { SyncHasMany } from "@ember-data/model";
import Model, { attr, belongsTo, hasMany } from "@ember-data/model";
import type ModelRegistry from "@ember-data/types/registries/model";
import type CompoundSurvey from "parog-web/models/compound-survey";
import type DmpHealthCentreConfig from "parog-web/models/dmp-health-centre-config";
import type HealthCentreLoincPreferredUnit from "parog-web/models/health-centre-loinc-preferred-unit";
import type LearningSection from "parog-web/models/learning-section";
import type Medic from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";
import type PrescriptionConfiguration from "parog-web/models/prescription-configuration";
import { sortBy } from "parog-web/utils/sort";
import type ValidIdentityDocument from "parog-web/models/valid-identity-document";
import type CareGiver from "./care-giver";
import type { HealthCentreRole } from "./health-centre-cohort";
import type HealthCentreTag from "./health-centre-tag";

export default class HealthCentre extends Model {
  static serviceToTherapeuticalAreaId = {
    renal: "nephro",
    pulmonary: "pneumo",
    cancer: "onco",
    psychiatry: "mental",
    hepatology: "hepato",
    cardiology: "cardio",
    diabetology: "diabeto",
    gastroenterology: "digest",
    generic: "generic"
  };

  /** A flag to show the apicrypt email used to send bio results */
  @attr("boolean") declare bioSenderDisplayed: boolean;
  @hasMany("care-giver", {
    async: false,
    inverse: "healthCentre"
  })
  declare careGivers: SyncHasMany<CareGiver>;
  @attr("string") declare city: string;
  @attr("string") declare code: string;
  /** A flag to deactivate the posibility for patient to decalre some bio results */
  @attr("boolean") declare disableBioForPatient: boolean;
  @belongsTo("dmp-health-centre-config", {
    async: false,
    inverse: "healthCentre"
  })
  declare dmpHealthCentreConfig: DmpHealthCentreConfig;
  /** A flag to activate the commentson the notification */
  @attr("boolean") declare enableCommentsOnNotifications: boolean;
  @attr("string") declare finess: string;
  @attr("boolean") declare hasRestrictedMessaging: boolean;
  @hasMany("health-centre-loinc-preferred-unit", {
    async: false,
    inverse: null
  })
  declare healthCentreLoincPreferredUnits: HealthCentreLoincPreferredUnit[];
  @attr("boolean") declare healthCentrePatientSearch: boolean;
  @hasMany("health-centre-tag", {
    async: false,
    inverse: "healthCentres"
  })
  declare healthCentreTags?: HealthCentreTag[];
  @attr("boolean") declare isgt: boolean;
  @attr("boolean") declare isMrcBordeaux: boolean;
  @attr("boolean") declare isPartOfIdentificationDomain: boolean;
  @hasMany("learning-section", { async: false, inverse: "healthCentre" })
  declare learningSections: SyncHasMany<LearningSection>;
  /** A flag to activate the LGC feature*/
  @attr("boolean") declare lgcEnabled: boolean;
  @hasMany("medic", { async: false, inverse: "healthCentres" })
  declare medics: Medic[];
  @attr("string") declare name: string;
  /** A flag to activate the new patient-history dashboard */
  @attr("boolean") declare patientHistoryDashboardEnabled: boolean;
  @hasMany("prescription-configuration", {
    async: false,
    inverse: "healthCentre"
  })
  declare prescriptionConfigurations: PrescriptionConfiguration[];
  /** A flag to activate the -pre-transplant folder feature for this health centre */
  @attr("boolean") declare preTransplant: boolean;
  @attr("string") declare productName: string;
  @attr("boolean") declare proIrc: boolean;
  @attr("string")
  declare service: keyof (typeof HealthCentre)["serviceToTherapeuticalAreaId"];
  /** a flag to show bio results that are in the PPS in priority */
  @attr("boolean") declare showImportantBioResultsSection: boolean;
  /** A flag to activate for DigestWise patients if they are in the control group */
  @attr("string") declare studyStatus: string;
  @hasMany("compound-survey", {
    async: false,
    inverse: "healthCentres"
  })
  declare surveys: CompoundSurvey[];
  /** A flag to deactivate the teleconsultation */
  @attr("boolean") declare teleconsultationDisabled: boolean;
  /** A flag to activate the toucan dashboard */
  @attr("boolean") declare toucanEnabled: boolean;
  @attr("string") declare uuid: string;
  /** Empty until `loadValidIdentityDocuments` loads the list */
  @hasMany("valid-identity-document", {
    async: false,
    inverse: "healthCentre"
  })
  declare validIdentityDocuments: ValidIdentityDocument[];

  get orderedLearningSections(): Array<LearningSection> {
    //@ts-expect-error TODO: update the learningSections type when @types/ember-data will be updated
    return this.learningSections.toSorted(sortBy("position"));
  }

  get productNameCapitalized() {
    return this.productName.replace(
      /^(.*?)(wise)$/,
      (_match, p1, p2) =>
        p1.charAt(0).toUpperCase() +
        p1.slice(1) +
        p2.charAt(0).toUpperCase() +
        p2.slice(1)
    );
  }

  get shouldShowHlaContent(): boolean {
    const hlaUUIDHealthCentres = [
      "a9a0599c-2864-40d3-a98f-4d2137966386", // Hôpital Bicêtre AP-HP
      "f38415fb-c2a5-4ac2-a1a8-221be175c13e", // Necker Adultes
      "78024f08-8642-4584-a927-ec75f0bcae55", // APHP - La Pitié Salpêtrière
      "f0895652-6bf6-447a-b67f-805a7cc77f21", // CH RENE DUBOS - Pontoise
      "51f1ccc8-3417-4d09-9e32-47a151a48cde", // Hôpital Européen Georges-Pompidou
      "ce022ac5-3cb9-4e10-a520-a397741f0bf3" // Hôpital Foch
    ];
    return hlaUUIDHealthCentres.includes(this.uuid) && this.service === "renal";
  }

  // Try to find a TherapeuticalArea record from the `service` attribte,
  // either by mapping the service to its corresponding therapeuticalArea Id
  // or just with the service name
  get therapeuticalArea(): ModelRegistry["therapeutical-area"] | null {
    const areaId =
      HealthCentre.serviceToTherapeuticalAreaId[this.service] ?? this.service;
    return areaId ? this.store.peekRecord("therapeutical-area", areaId) : null;
  }

  getRole(patient: Patient): HealthCentreRole | undefined {
    return patient.healthCentreCohorts.find(hcp => hcp.healthCentre == this)
      ?.role;
  }

  preferredUnitForLoincId(loincId) {
    return this.healthCentreLoincPreferredUnits.find(
      healthCentreLoincPreferredUnit =>
        healthCentreLoincPreferredUnit.loincId == loincId
    )?.preferredUnit;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "health-centre": HealthCentre;
  }
}
